import { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { login as apiLogin, getStoredToken } from '../api';
import { useApp } from '../contexts/AppContext';
import './Login.css';

const BLOCKS = [
  {
    no: '01',
    tag: 'Software',
    name: 'AI-Native ERP',
    desc: 'The system of record, rebuilt to run itself.',
    benefits: [
      'Posts, matches and reconciles in real time',
      'Catches bad data the moment it enters',
    ],
  },
  {
    no: '02',
    tag: 'Services',
    name: 'Workflow Automation',
    desc: 'The back-office work ERPs never touched.',
    benefits: [
      'Runs continuously, on its own schedule',
      'One reviewer covers a whole back office',
    ],
  },
  {
    no: '03',
    tag: 'Labor',
    name: 'Resolve',
    desc: 'Judgment where only people could go.',
    benefits: [
      'Finds the root cause, then decides',
      'Cited, governed and fully audited',
    ],
  },
];

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { onLoginSuccess } = useApp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // If already authenticated, redirect
  useEffect(() => {
    if (getStoredToken()) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  // Check for success messages from password reset
  useEffect(() => {
    if (searchParams.get('reset') === 'success') {
      setSuccessMsg('Password reset successfully. Please sign in with your new password.');
    }
  }, [searchParams]);

  const returnUrl = (location.state as any)?.from || '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMsg('');
    setLoading(true);

    try {
      await apiLogin(email, password);
      onLoginSuccess();
      navigate(returnUrl, { replace: true });
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;

      if (status === 401) {
        setError('Invalid email or password');
      } else if (status === 423) {
        setError(detail || 'Account locked. Please try again later.');
      } else if (status === 403) {
        setError(detail || 'Access denied');
      } else {
        setError('Unable to connect to server. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <section className="lp-brand">
        <header className="lp-brand-top">
          <div className="lp-wordmark">
            <span className="lp-sq" aria-hidden />
            AGENT WORKFORCE
          </div>
          <span className="lp-meta">agentworkforce.co</span>
        </header>

        <div className="lp-hero">
          <p className="lp-kicker">Built in three blocks</p>
          <h1 className="lp-headline">
            Software, services and labor, <em>disrupted on one engine.</em>
          </h1>
          <p className="lp-lede">
            Records the work, runs the work, resolves the work. No ERP,
            automation tool, or outsourcer does all three.
          </p>
        </div>

        <div className="lp-blocks">
          {BLOCKS.map(b => (
            <article key={b.no} className="lp-bk">
              <span className="lp-bk-no">{b.no}</span>
              <span className="lp-bk-tag">{b.tag}</span>
              <div className="lp-bk-name">{b.name}</div>
              <p className="lp-bk-desc">{b.desc}</p>
              <ul className="lp-bk-list">
                {b.benefits.map(x => (
                  <li key={x}>{x}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>

        <div className="lp-strip">
          <span>One data model</span>
          <span>One governance pipeline</span>
          <span>One audit trail</span>
        </div>
      </section>

      <section className="lp-form-wrap">
        <form className="lp-form" onSubmit={handleSubmit}>
          <p className="lp-form-kicker">Secure access</p>
          <h2 className="lp-form-title">Sign in</h2>
          <p className="lp-form-sub">Enter your credentials to access the platform.</p>

          {successMsg && <div className="lp-msg ok">{successMsg}</div>}
          {error && <div className="lp-msg err">{error}</div>}

          <div className="lp-field">
            <label htmlFor="email" className="lp-label">Email</label>
            <input
              id="email"
              type="email"
              className="lp-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@company.com"
              required
              autoFocus
              autoComplete="email"
            />
          </div>

          <div className="lp-field">
            <label htmlFor="password" className="lp-label">Password</label>
            <div className="lp-pw">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className="lp-input"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                className="lp-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>

          <div className="lp-opts">
            <button
              type="button"
              className="lp-forgot"
              onClick={() => navigate('/forgot-password')}
            >
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            className="lp-submit"
            disabled={loading || !email || !password}
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>
      </section>
    </div>
  );
}
